import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, MenuItem,
  Snackbar, Alert, CircularProgress, Divider, Stack, Avatar,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BusinessIcon from '@mui/icons-material/Business';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CallIcon from '@mui/icons-material/Call';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { followUpApi, marketingApi } from '../api/apiClient';
import PageShell from '../common/PageShell';

const emptyForm = {
  description: '',
  followUpDate: '',
  mode: 'CALLING',
  latitude: '',
  longitude: '',
  marketingId: '',
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, '& svg': { fontSize: 18 } }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

const FollowUpForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [marketingList, setMarketingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [originalCoords, setOriginalCoords] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fetchedRef = useRef(false);

  const info = JSON.parse(localStorage.getItem('device')) || null;

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    marketingApi
      .getAll()
      .then((res) => setMarketingList(res.data || []))
      .catch(() => setMarketingList([]));

    if (isEdit) {
      followUpApi.getById(id).then((res) => {
        const d = res.data;
        setForm({
          description: d.description || '',
          followUpDate: d.followUpDate ? d.followUpDate.slice(0, 16) : '',
          mode: d.mode || 'CALLING',
          latitude: d.latitude ?? '',
          longitude: d.longitude ?? '',
          marketingId: d.marketingId || '',
        });
        if (d.latitude != null && d.longitude != null) {
          setOriginalCoords({ latitude: d.latitude, longitude: d.longitude });
        }
      });
    } else {
      const mid = searchParams.get('marketingId');
      if (mid) setForm((f) => ({ ...f, marketingId: mid }));
    }
  }, [id, isEdit, searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!info && !isEdit) {
      setSnackbar({
        open: true,
        message:
          'Location access is required. Please enable location permission to continue.',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    const payload = {
      description: form.description,
      followUpDate: form.followUpDate || null,
      mode: form.mode,
      latitude: originalCoords?.latitude ?? info?.latitude ?? null,
      longitude: originalCoords?.longitude ?? info?.longitude ?? null,
      marketingId: Number(form.marketingId),
      createdAt: new Date().toISOString(),
    };

    try {
      if (isEdit) await followUpApi.update(id, payload);
      else await followUpApi.create(form.marketingId, payload);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
      localStorage.removeItem('device');
      setTimeout(() => navigate('/follow-ups'), 1000);
    } catch {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const hasLocation = originalCoords || info;

  return (
    <PageShell
      icon={isEdit ? <EditIcon /> : <AddCircleIcon />}
      title={isEdit ? 'Edit Follow-Up' : 'New Follow-Up'}
      subtitle={
        isEdit
          ? 'Update the follow-up details below'
          : 'Log a call, visit or meeting with a company'
      }
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Follow-Ups', to: '/follow-ups' },
        { label: isEdit ? 'Edit' : 'New' },
      ]}
      actions={
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/follow-ups')}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
          variant="outlined"
        >
          Back
        </Button>
      }
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <SectionHeader
                icon={<EventNoteIcon />}
                title="Follow-Up Details"
                subtitle="What happened and when"
              />

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Marketing / Company"
                    name="marketingId"
                    value={form.marketingId}
                    onChange={handleChange}
                    required
                    size="small"
                  >
                    <MenuItem value="">— Select a company —</MenuItem>
                    {marketingList.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.companyName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mode"
                    name="mode"
                    value={form.mode}
                    onChange={handleChange}
                    required
                    size="small"
                  >
                    <MenuItem value="VISITING">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MeetingRoomIcon fontSize="small" color="primary" />
                        <span>Visiting</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="CALLING">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CallIcon fontSize="small" color="secondary" />
                        <span>Calling</span>
                      </Stack>
                    </MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Follow-Up Date"
                    name="followUpDate"
                    type="datetime-local"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={form.followUpDate}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={5}
                    value={form.description}
                    onChange={handleChange}
                    required
                    size="small"
                    placeholder="Summarise the outcome, next steps, or notes..."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<BusinessIcon />}
                  title="Context"
                  subtitle="Auto-captured on save"
                />
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                      sx={{
                        bgcolor: hasLocation ? 'success.main' : 'warning.main',
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 16 },
                      }}
                    >
                      <LocationOnIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {hasLocation ? 'Location ready' : 'Location not available'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {originalCoords
                          ? `Using saved: ${originalCoords.latitude}, ${originalCoords.longitude}`
                          : info
                          ? `Device: ${info.latitude}, ${info.longitude}`
                          : 'Enable location to attach coordinates to this record.'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px dashed',
                  borderColor: 'info.light',
                  bgcolor: 'info.50',
                  display: 'flex',
                  gap: 1.5,
                }}
              >
                <InfoOutlinedIcon sx={{ color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Location is captured automatically from your device and cannot be edited
                  manually.
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/follow-ups')}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={!loading && <SaveIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isEdit ? (
                    'Update Follow-Up'
                  ) : (
                    'Create Follow-Up'
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default FollowUpForm;